import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, model, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { MunicipioResponse } from '@/core/api/sicom-api/registro/medico/municipio/municipio.model';
import { MunicipioService } from '@/core/api/sicom-api/registro/medico/municipio/municipio.service';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
const COMBINING_MARKS_START = 0x0300;
const COMBINING_MARKS_END = 0x036f;
const DIACRITICOS = new RegExp(`[${String.fromCharCode(COMBINING_MARKS_START)}-${String.fromCharCode(COMBINING_MARKS_END)}]`, 'g');

/** Normaliza texto para busca acento/caixa-insensível (mesma regra usada no backend via TRANSLATE). */
function normalizar(texto: string): string {
    return texto.normalize('NFD').replace(DIACRITICOS, '').toUpperCase();
}

/**
 * Diálogo de busca de Município (aba "Outros Filtros" do Relatório por Seleção) — espelha
 * `UntPesquisaMunicipio.pas` do legado: carrega o catálogo completo uma vez e filtra
 * incrementalmente pelo nome digitado (client-side, acento e caixa insensível).
 */
@Component({
    selector: 'app-pesquisa-municipio',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DialogoPadraoDirective, FormsModule, Button, Dialog, InputText, TableModule],
    templateUrl: './pesquisa-municipio.html'
})
export class PesquisaMunicipio {
    private readonly municipioService = inject(MunicipioService);
    private readonly destroyRef = inject(DestroyRef);
    private carregado = false;

    /** Controla a visibilidade do diálogo; suporta two-way binding via `[(visible)]`. */
    readonly visible = model<boolean>(false);
    /** Emite o município escolhido quando o usuário confirma. */
    readonly confirmado = output<MunicipioResponse>();

    readonly filtro = signal('');
    readonly carregando = signal(false);
    readonly municipios = signal<MunicipioResponse[]>([]);
    readonly selecionado = signal<MunicipioResponse | null>(null);

    readonly municipiosFiltrados = computed(() => {
        const termo = normalizar(this.filtro());
        if (!termo) return this.municipios();
        return this.municipios().filter((m) => normalizar(`${m.dsMunicipio} - ${m.uf}`).includes(termo));
    });

    /** Carrega o catálogo de municípios na primeira exibição do diálogo (cache simples em memória). */
    onShow(): void {
        if (this.carregado) return;
        this.carregado = true;
        this.carregando.set(true);
        this.municipioService
            .listarMunicipios()
            .pipe(
                catchError(() => of<MunicipioResponse[]>([])),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((lista) => {
                this.municipios.set(lista);
                this.carregando.set(false);
            });
    }

    selecionar(m: MunicipioResponse): void {
        this.selecionado.set(m);
    }

    confirmar(): void {
        const m = this.selecionado();
        if (!m) return;
        this.confirmado.emit(m);
        this.fechar();
    }

    cancelar(): void {
        this.fechar();
    }

    private fechar(): void {
        this.visible.set(false);
        this.filtro.set('');
        this.selecionado.set(null);
    }
}
